import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import {
  Zap,
  TrendingUp,
  Target,
  Brain,
  Landmark,
  Shield,
  Lock,
  Sparkles,
  ArrowRight,
  CheckCircle2,
  AlertTriangle,
  Crown,
  ArrowLeft,
  Trophy,
} from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { useSimulatorI18n } from "../i18n/simulator";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { ASSETS } from "../assets";

import { API_BASE } from "../lib/api"; 
import { safeJson } from "../lib/api";
const MODE_STYLES = [
  { accent: "#00d4ff", icon: TrendingUp, ring: "ring-cyan-400/30", glow: "shadow-[0_0_40px_rgba(0,212,255,0.18)]", border: "border-cyan-400/30", text: "text-cyan-300", bg: "bg-cyan-400/10" },
  { accent: "#f5a623", icon: Target, ring: "ring-amber-400/30", glow: "shadow-[0_0_40px_rgba(245,166,35,0.18)]", border: "border-amber-400/30", text: "text-amber-300", bg: "bg-amber-400/10" },
  { accent: "#ff3355", icon: Brain, ring: "ring-rose-400/30", glow: "shadow-[0_0_40px_rgba(255,51,85,0.18)]", border: "border-rose-400/30", text: "text-rose-300", bg: "bg-rose-400/10" },
  { accent: "#9b59ff", icon: Landmark, ring: "ring-purple-400/30", glow: "shadow-[0_0_40px_rgba(155,89,255,0.18)]", border: "border-purple-400/30", text: "text-purple-300", bg: "bg-purple-400/10" },
];

const StatCounter = ({ value, label, delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [n, setN] = useState(0);
  const numericMatch = String(value).match(/(\d+)/);
  const target = numericMatch ? parseInt(numericMatch[1], 10) : 0;
  const suffix = String(value).replace(/^\d+/, "");

  useEffect(() => {
    if (!inView) return;
    const t0 = performance.now();
    const dur = 1000;
    let raf;
    const step = (t) => {
      const k = Math.min(1, (t - t0) / dur);
      const eased = 1 - Math.pow(1 - k, 3);
      setN(Math.round(target * eased));
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, target]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 14 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay }}
      className="flex flex-col items-center border-r border-white/10 px-4 py-6 last:border-r-0 md:px-6"
    >
      <span className="font-display text-3xl font-black tracking-tighter text-white md:text-5xl">
        {n}
        {suffix}
      </span>
      <span className="mt-2 text-center text-[10px] font-bold uppercase tracking-[0.22em] text-white/45 md:text-xs">
        {label}
      </span>
    </motion.div>
  );
};

const DNABar = ({ label, idx }) => {
  // Decorative preview percentages (no real data on landing)
  const pcts = [78, 64, 71, 82, 58, 69];
  const colors = ["#00d4ff", "#00e676", "#f5a623", "#9b59ff", "#ff3355", "#00d4ff"];
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <div ref={ref} className="space-y-2">
      <div className="flex items-baseline justify-between">
        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-white/70 md:text-sm">{label}</span>
        <span className="font-mono text-xs text-white/40">{pcts[idx]}/100</span>
      </div>
      <div className="relative h-2 w-full overflow-hidden rounded-full bg-white/[0.06]">
        <motion.div
          initial={{ width: 0 }}
          animate={inView ? { width: `${pcts[idx]}%` } : {}}
          transition={{ duration: 1.2, ease: [0.33, 1, 0.68, 1], delay: idx * 0.08 }}
          className="absolute inset-y-0 left-0 rounded-full"
          style={{
            background: `linear-gradient(90deg, ${colors[idx]}, ${colors[idx]}aa)`,
            boxShadow: `0 0 12px ${colors[idx]}66`,
          }}
        />
      </div>
    </div>
  );
};

export default function SimulatorPage() {
  const { lang } = useLanguage();
  const t = useSimulatorI18n(lang);
  const { user, isPro, openLogin } = useAuth();
  const navigate = useNavigate();
  const [showPaywall, setShowPaywall] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const simRemaining = user?.trial?.simulator_remaining ?? 1;
  const hasFreeRun = !isPro && simRemaining > 0;

  const handleLaunch = () => {
    if (!user || user === false) return openLogin();
    if (isPro || hasFreeRun) navigate("/simulator/play");
    else setShowPaywall(true);
  };

  const handleUnlockPro = async () => {
    if (!user || user === false) return openLogin();
    if (isPro) return navigate("/simulator/play");
    try {
      setCheckoutLoading(true);
      const API = API_BASE;  // runtime-resolved, see /lib/api.js
      const token = localStorage.getItem("rebel_jwt");
      const res = await fetch(`${API}/api/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ plan: "pro_lifetime", origin_url: window.location.origin }),
      });
      // Clone before reading (defends against platform fetch interceptors).
      const data = await res.clone().json();
      if (data.url) window.location.href = data.url;
    } catch (e) {
      console.error(e);
    } finally {
      setCheckoutLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#05060d] text-white antialiased" data-testid="simulator-landing">
      {/* Global scanlines overlay (very subtle, page-scoped) */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 z-[1]"
        style={{
          background:
            "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,212,255,0.012) 2px, rgba(0,212,255,0.012) 4px)",
        }}
      />
      <Navbar />

      {/* ============ HERO ============ */}
      <section className="relative w-full overflow-hidden pb-16 pt-32 md:pt-40">
        {/* Animated cyan grid */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.18]"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(0,212,255,0.45) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,212,255,0.45) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
            maskImage: "radial-gradient(circle at 50% 0%, black, transparent 70%)",
          }}
        />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_-10%,rgba(0,212,255,0.22),transparent_60%)]" />
        <div className="absolute inset-x-0 top-32 mx-auto h-px max-w-5xl bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent" />

        <div className="relative mx-auto max-w-6xl px-6 md:px-12">
          <Link
            to="/academy"
            className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-white/50 transition-colors hover:text-white"
            data-testid="sim-back-academy"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Academia RE₿EL
          </Link>

          {/* Live badge */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mt-6 inline-flex items-center gap-2 rounded-full border border-emerald-400/40 bg-emerald-400/10 px-3 py-1.5 backdrop-blur"
            data-testid="sim-live-badge"
          >
            <span className="relative inline-flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400/80" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
            </span>
            <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-emerald-300 md:text-xs">
              {t.eyebrow}
            </span>
          </motion.div>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="mt-5 font-display text-5xl font-black leading-[0.95] tracking-[-0.03em] text-white sm:text-6xl lg:text-[5.5rem]"
            data-testid="sim-title"
          >
            {t.title}
            <br />
            <em className="not-italic" style={{ color: "#00d4ff", textShadow: "0 0 36px rgba(0,212,255,0.45)" }}>
              {t.title_em}
            </em>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-6 max-w-2xl text-base leading-relaxed text-white/65 md:text-xl"
          >
            {t.subtitle}
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.22 }}
            className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
          >
            {isPro ? (
              <button
                onClick={handleLaunch}
                className="group relative inline-flex items-center justify-center gap-3 overflow-hidden rounded-full bg-gradient-to-r from-emerald-400 to-cyan-400 px-7 py-4 text-sm font-bold uppercase tracking-wider text-black transition-all hover:-translate-y-0.5 hover:shadow-[0_0_50px_rgba(0,230,118,0.45)] md:text-base"
                data-testid="sim-cta-launch-pro"
              >
                <Crown className="h-4 w-4" />
                <span>{t.open_play}</span>
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/40 to-transparent transition-transform duration-1000 group-hover:translate-x-full" />
              </button>
            ) : (
              <button
                onClick={handleLaunch}
                className="group relative inline-flex items-center justify-center gap-3 overflow-hidden rounded-full border border-cyan-400/50 bg-cyan-400/10 px-7 py-4 text-sm font-bold uppercase tracking-wider text-cyan-100 backdrop-blur-md transition-all hover:-translate-y-0.5 hover:border-cyan-300 hover:bg-cyan-400/20 hover:text-white hover:shadow-[0_0_40px_rgba(0,212,255,0.4)] md:text-base"
                data-testid="sim-cta-free"
              >
                <Zap className="h-4 w-4" />
                <span>{hasFreeRun ? t.cta_free : t.free_used}</span>
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
            )}

            {!isPro && (
              <button
                onClick={handleUnlockPro}
                disabled={checkoutLoading}
                className="group relative inline-flex items-center justify-center gap-3 overflow-hidden rounded-full bg-white px-7 py-4 text-sm font-bold uppercase tracking-wider text-black transition-all hover:-translate-y-0.5 hover:shadow-[0_0_40px_rgba(255,255,255,0.3)] disabled:opacity-60 md:text-base"
                data-testid="sim-cta-pro"
              >
                <Lock className="h-4 w-4" />
                <span>{t.cta_pro}</span>
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
            )}
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.35 }}
            className="mt-5 text-[11px] uppercase tracking-[0.22em] text-white/35"
          >
            {isPro ? t.pro_active : t.cta_note}
          </motion.p>

          {/* Leaderboard link */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.45 }}
            className="mt-8"
          >
            <Link
              to="/simulator/leaderboard"
              data-testid="sim-leaderboard-link"
              className="group inline-flex items-center gap-2 rounded-full border border-amber-300/40 bg-amber-300/[0.06] px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-amber-200 backdrop-blur-md transition-all hover:-translate-y-0.5 hover:border-amber-300/70 hover:bg-amber-300/15 hover:text-white hover:shadow-[0_0_24px_rgba(255,215,0,0.3)]"
            >
              <Trophy className="h-3.5 w-3.5" />
              <span>Leaderboard global</span>
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </div>

        {/* ============ STATS BAR ============ */}
        <div className="relative mx-auto mt-20 max-w-6xl px-6 md:px-12">
          <div
            className="grid grid-cols-2 overflow-hidden rounded-2xl border border-white/10 bg-black/40 backdrop-blur-xl md:grid-cols-4"
            data-testid="sim-stats-bar"
          >
            {t.stats.map((s, i) => (
              <StatCounter key={i} value={s.value} label={s.label} delay={i * 0.08} />
            ))}
          </div>
        </div>
      </section>

      {/* ============ MODES GRID ============ */}
      <section className="relative w-full bg-[#05060d] py-24">
        <div className="mx-auto max-w-6xl px-6 md:px-12">
          <div className="flex items-baseline justify-between">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-cyan-300/80">
                {t.eyebrow}
              </p>
              <h2 className="mt-3 font-display text-3xl font-black tracking-tighter text-white sm:text-4xl md:text-5xl">
                {t.modes_title}
              </h2>
              <p className="mt-3 max-w-xl text-sm text-white/55 md:text-base">
                {t.modes_subtitle}
              </p>
            </div>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2">
            {t.modes.map((m, i) => {
              const s = MODE_STYLES[i];
              const Icon = s.icon;
              const isPaid = m.badge !== "FREE";
              return (
                <motion.article
                  key={i}
                  initial={{ opacity: 0, y: 22 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                  className={`group relative overflow-hidden rounded-3xl border ${s.border} bg-gradient-to-br from-white/[0.04] via-white/[0.02] to-transparent p-7 backdrop-blur-xl transition-all hover:-translate-y-0.5 ${s.glow}`}
                  data-testid={`sim-mode-card-${i}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className={`flex h-12 w-12 items-center justify-center rounded-2xl border ${s.border} ${s.bg}`}>
                      <Icon className={`h-5 w-5 ${s.text}`} />
                    </div>
                    <span
                      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.2em] ${
                        isPaid
                          ? "border-amber-400/40 bg-amber-400/10 text-amber-200"
                          : "border-emerald-400/40 bg-emerald-400/10 text-emerald-200"
                      }`}
                    >
                      {isPaid ? <Lock className="h-3 w-3" /> : <Sparkles className="h-3 w-3" />}
                      {m.badge}
                    </span>
                  </div>
                  <h3 className="mt-5 font-display text-xl font-bold tracking-tight text-white md:text-2xl">
                    {m.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/55 md:text-[15px]">{m.desc}</p>
                  <div className="mt-6 flex items-center gap-2">
                    <span className="h-px flex-1 bg-gradient-to-r from-white/10 to-transparent" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/30">
                      0{i + 1} / 04
                    </span>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ TRADER DNA PREVIEW ============ */}
      <section className="relative w-full overflow-hidden bg-[#07080f] py-24">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_50%,rgba(155,89,255,0.14),transparent_55%)]" />
        <div className="relative mx-auto max-w-6xl px-6 md:px-12">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-purple-300/80">
                Trader DNA™
              </p>
              <h2 className="mt-3 font-display text-3xl font-black tracking-tighter text-white sm:text-4xl md:text-5xl">
                {t.dna_title}
              </h2>
              <p className="mt-4 max-w-xl text-sm text-white/60 md:text-base">
                {t.dna_subtitle}
              </p>
              <ul className="mt-8 space-y-3 text-sm text-white/65">
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                  6 métricas psicológicas evaluadas por IA
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                  Ranking institucional desbloqueado
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                  Análisis personalizado de fortalezas y sesgos
                </li>
              </ul>
            </div>

            <div className="relative rounded-3xl border border-white/10 bg-black/40 p-6 backdrop-blur-xl md:p-8">
              <div className="mb-5 flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
                  ◈ DNA PROFILE
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-purple-400/40 bg-purple-400/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-purple-200">
                  <Shield className="h-3 w-3" />
                  Preview
                </span>
              </div>
              <div className="space-y-5">
                {t.dna_metrics.map((label, i) => (
                  <DNABar key={i} label={label} idx={i} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="relative w-full overflow-hidden bg-[#05060d] py-28">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(0,212,255,0.16),transparent_60%)]" />
        <div className="relative mx-auto max-w-3xl px-6 text-center md:px-12">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-cyan-300/80">
            {t.cta_eyebrow}
          </p>
          <div className="mt-4 flex items-baseline justify-center gap-4">
            <span className="font-display text-3xl font-black tracking-tighter text-white/30 line-through md:text-5xl">
              {t.cta_price_old}
            </span>
            <span className="font-display text-5xl font-black tracking-tighter text-white md:text-7xl">
              {t.cta_price_new}
            </span>
          </div>
          <p className="mt-5 text-sm text-white/55 md:text-base">{t.cta_note_final}</p>

          <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
            {isPro ? (
              <button
                onClick={() => navigate("/simulator/play")}
                className="group inline-flex items-center justify-center gap-3 rounded-full bg-gradient-to-r from-emerald-400 to-cyan-400 px-8 py-4 text-sm font-bold uppercase tracking-wider text-black transition-all hover:-translate-y-0.5 hover:shadow-[0_0_50px_rgba(0,230,118,0.45)] md:text-base"
                data-testid="sim-final-cta-pro"
              >
                <Crown className="h-4 w-4" />
                {t.open_play}
              </button>
            ) : (
              <button
                onClick={handleUnlockPro}
                disabled={checkoutLoading}
                className="group inline-flex items-center justify-center gap-3 rounded-full bg-white px-8 py-4 text-sm font-bold uppercase tracking-wider text-black transition-all hover:-translate-y-0.5 hover:shadow-[0_0_40px_rgba(255,255,255,0.3)] disabled:opacity-60 md:text-base"
                data-testid="sim-final-cta-unlock"
              >
                <Lock className="h-4 w-4" />
                {t.cta_btn_final}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Educational disclaimer */}
      <div className="mx-auto max-w-3xl px-6 pb-16 md:px-12">
        <div className="flex items-start gap-3 rounded-2xl border border-amber-400/25 bg-amber-400/[0.04] p-5">
          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-amber-400/30 bg-black/40">
            <AlertTriangle className="h-4 w-4 text-amber-300" />
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-amber-300">RE₿EL BTC</p>
            <p className="mt-1 text-sm text-white/60">
              RE₿EL BTC ofrece exclusivamente contenido educativo. No es asesoramiento financiero.
            </p>
          </div>
        </div>
      </div>

      <Footer />

      {/* Paywall modal */}
      {showPaywall && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 backdrop-blur-md"
          onClick={() => setShowPaywall(false)}
          data-testid="sim-paywall-modal"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-lg overflow-hidden rounded-3xl border border-cyan-400/30 bg-[#080a14] p-8 shadow-[0_0_60px_rgba(0,212,255,0.25)]"
          >
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-400/60 to-transparent" />
            <div className="inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1">
              <Lock className="h-3.5 w-3.5 text-amber-300" />
              <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-200">
                {t.free_used}
              </span>
            </div>
            <h3 className="mt-5 font-display text-3xl font-black tracking-tight text-white">
              {t.paywall_title}
            </h3>
            <ul className="mt-6 space-y-3 text-sm text-white/70">
              {t.paywall_features.map((f, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                  {f}
                </li>
              ))}
            </ul>
            <div className="mt-7 flex items-baseline gap-3">
              <span className="font-display text-2xl font-black tracking-tighter text-white/30 line-through">
                {t.cta_price_old}
              </span>
              <span className="font-display text-4xl font-black tracking-tighter text-white">
                {t.cta_price_new}
              </span>
            </div>
            <button
              onClick={handleUnlockPro}
              disabled={checkoutLoading}
              className="mt-6 inline-flex w-full items-center justify-center gap-3 rounded-full bg-white px-6 py-3.5 text-sm font-bold uppercase tracking-wider text-black transition-all hover:-translate-y-0.5 hover:shadow-[0_0_40px_rgba(255,255,255,0.3)] disabled:opacity-60"
              data-testid="sim-paywall-checkout"
            >
              <Lock className="h-4 w-4" />
              {t.cta_btn_final}
            </button>
            <button
              onClick={() => setShowPaywall(false)}
              className="mt-3 inline-flex w-full items-center justify-center text-xs uppercase tracking-[0.22em] text-white/40 transition-colors hover:text-white"
            >
              {t.paywall_close}
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
}
